import type { InvoiceSearchResult, Transaction } from "@/apis/gql/types";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { SelectCell } from "@/components/shared/cells/select";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { TransactionMatchCell } from "@/features/transactions/shared/invoice-matching-form/cells/invoice";

const selectColumn: ColumnDef<InvoiceSearchResult> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => {
    const isPinned = cell.row.getIsPinned();

    if (isPinned) {
      return null;
    }

    return (
      <div className="flex items-center justify-center h-10 w-full">
        <SelectCell {...cell} />
      </div>
    );
  },
  enableSorting: false,
  enableHiding: false,
};

const invoiceDataColumn = ({
  transactions,
  onMatch,
  onUnmatch,
  rowPinningOnMatch,
}: {
  transactions: Transaction[];
  onMatch: (invoiceIds: string[]) => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
  rowPinningOnMatch: boolean;
}): ColumnDef<InvoiceSearchResult> => ({
  id: "invoiceData",
  accessorKey: "invoiceData",
  meta: {
    className: "p-0 overflow-hidden mb-[3px]",
  },
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("invoices")} />
    );
  },
  cell: (cell) => {
    return (
      <TransactionMatchCell
        cell={cell}
        transactions={transactions}
        onMatch={onMatch}
        onUnmatch={onUnmatch}
        rowPinningOnMatch={rowPinningOnMatch}
      />
    );
  },
});

const supplierNameColumn: ColumnDef<InvoiceSearchResult> = {
  id: "supplierName",
  accessorKey: "supplierName",
};

const invoiceNoColumn: ColumnDef<InvoiceSearchResult> = {
  id: "invoiceNo",
  accessorKey: "invoiceNo",
};

const invoiceTotalAmountColumn: ColumnDef<InvoiceSearchResult> = {
  id: "invoiceTotalAmount",
  accessorKey: "totalAmount",
};

const invoiceDateColumn = {
  id: "invoiceDate",
  accessorKey: "invoiceDate",
};

export const getInvoiceMatchingColumns: (props: {
  transactions: Transaction[];
  onMatch: (invoiceIds: string[]) => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
  rowPinningOnMatch: boolean;
}) => ColumnDef<InvoiceSearchResult>[] = ({
  transactions,
  onMatch,
  onUnmatch,
  rowPinningOnMatch,
}) => {
  return [
    selectColumn,
    invoiceDataColumn({ transactions, onMatch, onUnmatch, rowPinningOnMatch }),
    supplierNameColumn,
    invoiceNoColumn,
    invoiceTotalAmountColumn,
    invoiceDateColumn,
  ];
};
